import { useEffect } from "react";

import * as facemesh from "resmed-face-landmarks-detection";
import * as tf from "@tensorflow/tfjs-core";

import { GLASSES_CHECK_ENABLED } from "./FaceMeasurer/MugShotConfig";

/**
 *
 * @param loadIrisModel Should we load the iris model?
 *
 * @param modelUrl  The facemesh model without iris information.  Set this value to null to load the model from tfhub.
 *                  https://tfhub.dev/mediapipe/tfjs-model/facemesh/1/default/1
 *
 * @param irisModelUrl  The facemesh iris model. Set this value to null to load the model from tfhub.
 *                       https://tfhub.dev/mediapipe/tfjs-model/iris/1/default/2
 *
 * @param blazeFaceModelUrl  The BlazeFace model. Set this value to null to load the model from tfhub.
 *                       https://tfhub.dev/tensorflow/tfjs-model/blazeface/1/default/1
 *
 * @param glassesModelUrl  The glasses detection model. Set this value to null to load the model from
 *            https://dkzjck98luqv5.cloudfront.net/unitTestModels/resmed/glasses/1/model.json
 *
 * @returns {Promise<MediaPipeFaceMesh>}  A promise for a constructed pipeline.
 */
export async function loadModelPipeline(
    loadIrisModel,
    modelUrl = "/mlmodel/google/mediapipe/tfjs-model/facemesh/1/default/1/model.json",
    irisModelUrl = "/mlmodel/google/mediapipe/tfjs-model/iris/1/default/2/model.json",
    blazeFaceModelUrl = "/mlmodel/google/mediapipe/tfjs-model/blazeface/1/default/1/model.json",
    glassesModelUrl = "/mlmodel/glasses/model.json"
) {
    return await facemesh.load(facemesh.SupportedPackages.mediapipeFacemesh, {
    shouldLoadIrisModel: loadIrisModel,
    maxFaces: 1,
    modelUrl: modelUrl,
    irisModelUrl: irisModelUrl,
    blazeFaceModelUrl: blazeFaceModelUrl,
    glassesModelUrl: glassesModelUrl,
    shouldLoadGlassesModel: GLASSES_CHECK_ENABLED
    });
}

// We only want to load the model once for the entire application.
// This variable is set to 'true' when the model is loaded, and
// remains that way for the lifecycle of the application.
let isLoadingModel = false

// TODO: Refactor component out to a custom hook or plain-function
// I don't think this needs to be a component, since it only has logic
// and doesn't return any JSX.
const MugshotModelLoader = ({
    model,
    setModel,
    loadingPromise,
    setLoadingPromise
}) => {
    
    const loadModel = async () => {
        if (tf.getBackend() !== "wasm") {
          await tf.setBackend("wasm");
          await tf.ready()
        }
    
        if (model !== null) {
          // Model is already loaded.  Nothing to load.
        } else {
          let values = await Promise.all([loadModelPipeline(true)]);
    
          setModel(values[0]);
        }
      };

    useEffect(() => {
        if (!isLoadingModel) {
            isLoadingModel = true;
            setLoadingPromise(loadModel());
        }
    }, []);

    return null;
};

export default MugshotModelLoader;
